import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { USER_ROLE_LABEL, USER_STATUS_LABEL } from '../constants/labels';
import type { ManagedUser, UpdateUserForm, UserRole, UserStatus } from '../types/admin';

export function UserEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<ManagedUser | null>(null);
  const [form, setForm] = useState<UpdateUserForm>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api
      .get<ManagedUser>(`/users/${id}`)
      .then(({ data }) => {
        setUser(data);
        setForm({
          role: data.role,
          status: data.status,
          storeCreationLimit: data.storeCreationLimit,
          premiumStartedAt: data.premiumStartedAt ? data.premiumStartedAt.slice(0, 10) : '',
        });
        setError('');
      })
      .catch(() => {
        setError('加载用户失败');
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = <K extends keyof UpdateUserForm>(
    key: K,
    value: UpdateUserForm[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    setError('');
    setSuccess(false);

    const limit = form.storeCreationLimit;
    if (
      limit !== undefined &&
      (!Number.isSafeInteger(limit) || limit < 0)
    ) {
      setError('门店创建额度必须是非负整数');
      setSaving(false);
      return;
    }

    try {
      const payload: UpdateUserForm = {
        ...form,
        storeCreationLimit: limit === undefined ? undefined : Number(limit),
        premiumStartedAt: form.premiumStartedAt?.trim() ?? '',
      };
      await api.put(`/users/${id}`, payload);
      setSuccess(true);
      setError('');
    } catch {
      setError('保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100"
        >
          返回
        </button>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">编辑账号</h2>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500">加载中…</div>
      ) : error ? (
        <div className="text-center py-10 text-red-600">{error}</div>
      ) : user ? (
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl shadow p-4 sm:p-6 space-y-4"
        >
          <div className="text-sm text-gray-500 mb-2">
            手机号：<span className="font-medium text-gray-900">{user.mobile}</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">角色</label>
            <select
              value={form.role || ''}
              onChange={(e) => handleChange('role', e.target.value as UserRole)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-base"
            >
              <option value="owner">{USER_ROLE_LABEL.owner}</option>
              <option value="groomer">{USER_ROLE_LABEL.groomer}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">状态</label>
            <select
              value={form.status || ''}
              onChange={(e) => handleChange('status', e.target.value as UserStatus)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-base"
            >
              <option value="active">{USER_STATUS_LABEL.active}</option>
              <option value="disabled">{USER_STATUS_LABEL.disabled}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              门店创建额度
            </label>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              value={form.storeCreationLimit ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                handleChange(
                  'storeCreationLimit',
                  value === '' ? undefined : parseInt(value, 10),
                );
              }}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-base"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              会员开始日期
            </label>
            <input
              type="date"
              value={form.premiumStartedAt || ''}
              onChange={(e) => handleChange('premiumStartedAt', e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-base"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>
          )}
          {success && (
            <div className="text-sm text-green-700 bg-green-50 rounded-md px-3 py-2">
              保存成功
            </div>
          )}

          <div className="pt-2">
            <button
              type="submit"
              disabled={saving}
              className="w-full sm:w-auto px-6 py-3 rounded-lg bg-indigo-600 text-white font-medium text-base hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-60"
            >
              {saving ? '保存中…' : '保存'}
            </button>
          </div>
        </form>
      ) : (
        <div className="text-center py-10 text-gray-500">无法加载用户</div>
      )}
    </Layout>
  );
}
